import React, { useState } from 'react';
import Papa from 'papaparse';
import { Upload, Download, X, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { bulkCreateProducts, bulkCreateProductsFromCsv } from '../../services/admin/products';
import { getCategories } from '../../services/categories';
import { createCategory } from '../../services/admin/categories';

const ProductBulkUpload = ({ onSuccess }) => {
  const { toast } = useToast();
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [categories, setCategories] = useState([]);
  const [missingInfo, setMissingInfo] = useState(null);
  const [isMissingModalOpen, setIsMissingModalOpen] = useState(false);
  const [creatingMissing, setCreatingMissing] = useState(false);

  React.useEffect(() => {
    const flattenCategories = (items) => {
      const result = [];
      const walk = (list) => {
        list.forEach((item) => {
          result.push({
            ...item,
            id: String(item.id),
            parentId: item.parentId ? String(item.parentId) : null,
          });
          if (item.children?.length) {
            walk(item.children);
          }
        });
      };
      walk(items);
      return result;
    };

    const fetchCategories = async () => {
      try {
        const cats = await getCategories();
        setCategories(flattenCategories(cats));
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const normalizeText = (value) => (typeof value === 'string' ? value.trim() : value);
  const normalizeKey = (value) =>
    typeof value === 'string' ? value.trim().toLowerCase() : String(value || '').trim().toLowerCase();

  const generateSlug = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const resolveCategory = (list, value, parentId = null) => {
    if (!value) return null;
    const normalized = String(value).toLowerCase();
    return list.find((cat) => {
      const matchesValue =
        String(cat.id) === String(value) ||
        cat.slug?.toLowerCase() === normalized ||
        cat.name?.toLowerCase() === normalized;
      const matchesParent = parentId ? String(cat.parentId) === String(parentId) : true;
      return matchesValue && matchesParent;
    });
  };

  const splitList = (value) =>
    value ? String(value).split(',').map((item) => item.trim()).filter(Boolean) : [];

  const processParsedResults = (results, categoriesList) => {
    const validatedProducts = [];
    const errors = [];
    const missingCategories = new Map();
    const missingSubcategories = new Map();

    results.data.forEach((row, index) => {
      const rowErrors = [];
      const name = normalizeText(row.name);
      const description = normalizeText(row.description);
      const priceRaw = normalizeText(row.price);
      const originalPriceRaw = normalizeText(row.originalPrice);
      const categoryRaw = normalizeText(row.category);
      const subcategoryRaw = normalizeText(row.subcategory);
      const stockRaw = normalizeText(row.stock);

      // Validate required fields
      if (!name) rowErrors.push('Ürün adı eksik');
      if (priceRaw === undefined || priceRaw === '') rowErrors.push('Fiyat eksik');
      if (!categoryRaw) rowErrors.push('Kategori eksik');
      if (stockRaw === undefined || stockRaw === '') rowErrors.push('Stok eksik');

      const price = parseFloat(priceRaw);
      if (priceRaw !== undefined && priceRaw !== '' && Number.isNaN(price)) {
        rowErrors.push('Fiyat geçersiz');
      }

      const stock = parseInt(stockRaw, 10);
      if (stockRaw !== undefined && stockRaw !== '' && Number.isNaN(stock)) {
        rowErrors.push('Stok geçersiz');
      }

      if (rowErrors.length > 0) {
        errors.push(`Satır ${index + 2}: ${rowErrors.join(', ')}`);
        return;
      }

      const categoryMatch = resolveCategory(categoriesList, categoryRaw, null);
      if (!categoryMatch) {
        const key = normalizeKey(categoryRaw);
        if (!missingCategories.has(key)) {
          missingCategories.set(key, { name: categoryRaw });
        }
        // Kategori yokken alt kategori de eksikse kaydet; "Ekle ve Devam" hepsini oluşturacak
        if (subcategoryRaw) {
          const subKey = `${normalizeKey(categoryRaw)}::${normalizeKey(subcategoryRaw)}`;
          if (!missingSubcategories.has(subKey)) {
            missingSubcategories.set(subKey, { name: subcategoryRaw, parentName: categoryRaw });
          }
        }
        return;
      }

      const hasSubcategories = categoriesList.some(
        (cat) => String(cat.parentId) === String(categoryMatch.id)
      );
      const subcategoryMatch = subcategoryRaw
        ? resolveCategory(categoriesList, subcategoryRaw, categoryMatch.id)
        : null;
      if (hasSubcategories && !subcategoryMatch) {
        const key = `${normalizeKey(categoryMatch.name)}::${normalizeKey(subcategoryRaw)}`;
        if (!missingSubcategories.has(key)) {
          missingSubcategories.set(key, {
            name: subcategoryRaw,
            parentName: categoryMatch.name,
          });
        }
        return;
      }

      validatedProducts.push({
        name,
        description: description || '',
        price,
        originalPrice: originalPriceRaw ? parseFloat(originalPriceRaw) : null,
        category: categoryMatch.id,
        subcategory: subcategoryMatch ? subcategoryMatch.id : null,
        stock,
        sizes: splitList(row.sizes),
        colors: splitList(row.colors),
        colorNames: splitList(row.colorNames),
        material: row.material?.trim() || '',
        care: row.care?.trim() || '',
        isNew: row.isNew === 'true' || row.isNew === true,
        isBestSeller: row.isBestSeller === 'true' || row.isBestSeller === true,
        isFeatured: row.isFeatured === 'true' || row.isFeatured === true,
        onSale: row.onSale === 'true' || row.onSale === true,
        images: [], // Images need to be uploaded separately
      });
    });

    return {
      errors,
      validatedProducts,
      missing: {
        categories: Array.from(missingCategories.values()),
        subcategories: Array.from(missingSubcategories.values()),
      },
    };
  };

  const generateCSVTemplate = () => {
    const parentCategory = categories.find((cat) => !cat.parentId);
    const childCategory = parentCategory
      ? categories.find((cat) => cat.parentId === parentCategory.id)
      : null;
    const categoryValue = parentCategory?.slug || parentCategory?.name || 'kategori-slug';
    const subcategoryValue = childCategory?.slug || childCategory?.name || 'alt-kategori-slug';
    const headers = [
      'name',
      'description',
      'price',
      'originalPrice',
      'category',
      'subcategory',
      'stock',
      'sizes',
      'colors',
      'colorNames',
      'images',
      'material',
      'care',
      'isNew',
      'isBestSeller',
      'isFeatured',
      'onSale',
    ];

    const csvContent = [
      headers.join(','),
      `Örnek Ürün,Ürün açıklaması buraya,299.99,399.99,${categoryValue},${subcategoryValue},100,"XS,S,M,L","#FFFFFF,#000000","Beyaz,Siyah","https://cdn.ornek.com/urun-1.jpg,https://cdn.ornek.com/urun-2.jpg",%100 Pamuk,Soğuk suda yıkayın,true,false,true,true`,
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'gappaq-urun-template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        toast({
          title: 'Hata',
          description: 'Lütfen CSV dosyası seçin.',
          variant: 'destructive',
        });
        return;
      }
      setFile(selectedFile);
      setResults(null);
    }
  };

  const parseCSV = async () => {
    if (!file) return;

    setParsing(true);
    try {
      const csvText = await file.text();
      const detectDelimiter = (text) => {
        const headerLine = text.split(/\r?\n/).find((line) => line.trim());
        if (!headerLine) return ',';
        const commaCount = (headerLine.match(/,/g) || []).length;
        const semicolonCount = (headerLine.match(/;/g) || []).length;
        const tabCount = (headerLine.match(/\t/g) || []).length;
        if (semicolonCount > commaCount && semicolonCount > tabCount) return ';';
        if (tabCount > commaCount) return '\t';
        return ',';
      };

      const parseWithDelimiter = (delimiter) =>
        new Promise((resolve, reject) => {
          Papa.parse(csvText, {
            header: true,
            skipEmptyLines: true,
            encoding: 'UTF-8',
            delimiter,
            transformHeader: (header) => header.replace(/^\uFEFF/, '').trim(),
            complete: resolve,
            error: reject,
          });
        });

      let results = await parseWithDelimiter(detectDelimiter(csvText));
      const requiredHeaders = ['name', 'price', 'category', 'stock'];
      const hasRequiredHeaders =
        Array.isArray(results.meta?.fields) &&
        requiredHeaders.every((field) => results.meta.fields.includes(field));

      if (!hasRequiredHeaders) {
        results = await parseWithDelimiter(',');
      }

      try {
        const { errors, validatedProducts, missing } = processParsedResults(results, categories);

        if (errors.length > 0) {
          setResults({
            success: false,
            errors,
            validCount: validatedProducts.length,
            totalCount: results.data.length,
          });
          setParsing(false);
          return;
        }

        if (missing.categories.length > 0 || missing.subcategories.length > 0) {
          setMissingInfo(missing);
          setIsMissingModalOpen(true);
          setParsing(false);
          return;
        }

        setResults({
          success: true,
          products: validatedProducts,
          validCount: validatedProducts.length,
          totalCount: results.data.length,
        });
      } catch (error) {
        console.error('Error processing CSV:', error);
        toast({
          title: 'Hata',
          description: 'CSV dosyası işlenirken bir hata oluştu.',
          variant: 'destructive',
        });
      }
      setParsing(false);
    } catch (error) {
      console.error('Error parsing CSV:', error);
      toast({
        title: 'Hata',
        description: 'CSV dosyası işlenirken bir hata oluştu.',
        variant: 'destructive',
      });
      setParsing(false);
    }
  };

  const handleUpload = async () => {
    if (!results || !results.success) return;

    setUploading(true);
    try {
      if (file) {
        await bulkCreateProductsFromCsv(file);
      } else {
        // Upload in batches of 100
        const batchSize = 100;
        for (let i = 0; i < results.products.length; i += batchSize) {
          const batch = results.products.slice(i, i + batchSize);
          await bulkCreateProducts(batch);
        }
      }

      toast({
        title: 'Başarılı',
        description: `${results.validCount} ürün başarıyla yüklendi.`,
      });

      setFile(null);
      setResults(null);
      onSuccess();
    } catch (error) {
      console.error('Error uploading products:', error);
      toast({
        title: 'Hata',
        description: 'Ürünler yüklenirken bir hata oluştu.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCreateMissingCategories = async () => {
    if (!missingInfo) return;
    setCreatingMissing(true);
    try {
      const categoriesByKey = new Map();
      categories.forEach((cat) => {
        categoriesByKey.set(normalizeKey(cat.name), cat);
        categoriesByKey.set(normalizeKey(cat.slug), cat);
        categoriesByKey.set(String(cat.id), cat);
      });

      for (const item of missingInfo.categories) {
        const key = normalizeKey(item.name);
        if (categoriesByKey.has(key)) continue;
        const created = await createCategory({
          name: item.name,
          slug: generateSlug(item.name),
          parentId: null,
          description: '',
          image: '',
          order: 0,
          isActive: true,
        });
        categoriesByKey.set(normalizeKey(created.name), created);
        categoriesByKey.set(normalizeKey(created.slug), created);
        categoriesByKey.set(String(created.id), created);
      }

      for (const item of missingInfo.subcategories) {
        const parentKey = normalizeKey(item.parentName);
        let parentCategory = categoriesByKey.get(parentKey);
        if (!parentCategory) {
          parentCategory = await createCategory({
            name: item.parentName,
            slug: generateSlug(item.parentName),
            parentId: null,
            description: '',
            image: '',
            order: 0,
            isActive: true,
          });
          categoriesByKey.set(normalizeKey(parentCategory.name), parentCategory);
          categoriesByKey.set(normalizeKey(parentCategory.slug), parentCategory);
          categoriesByKey.set(String(parentCategory.id), parentCategory);
        }

        const subKey = `${normalizeKey(parentCategory.name)}::${normalizeKey(item.name)}`;
        if (categoriesByKey.has(subKey)) continue;
        const createdSub = await createCategory({
          name: item.name,
          slug: generateSlug(item.name),
          parentId: parentCategory.id,
          description: '',
          image: '',
          order: 0,
          isActive: true,
        });
        categoriesByKey.set(subKey, createdSub);
        categoriesByKey.set(normalizeKey(createdSub.name), createdSub);
        categoriesByKey.set(normalizeKey(createdSub.slug), createdSub);
      }

      const freshCategories = await getCategories();
      const flattenCategories = (items) => {
        const result = [];
        const walk = (list) => {
          list.forEach((item) => {
            result.push({
              ...item,
              id: String(item.id),
              parentId: item.parentId ? String(item.parentId) : null,
            });
            if (item.children?.length) {
              walk(item.children);
            }
          });
        };
        walk(items);
        return result;
      };
      const flat = flattenCategories(freshCategories);
      setCategories(flat);
      setIsMissingModalOpen(false);
      setMissingInfo(null);

      const csvText = await file.text();
      const parsed = Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        encoding: 'UTF-8',
        transformHeader: (header) => header.replace(/^\uFEFF/, '').trim(),
      });
      const { errors, validatedProducts, missing } = processParsedResults(parsed, flat);
      if (errors.length > 0) {
        setResults({
          success: false,
          errors,
          validCount: validatedProducts.length,
          totalCount: parsed.data.length,
        });
        return;
      }
      if (missing.categories.length > 0 || missing.subcategories.length > 0) {
        setMissingInfo(missing);
        setIsMissingModalOpen(true);
        return;
      }
      setResults({
        success: true,
        products: validatedProducts,
        validCount: validatedProducts.length,
        totalCount: parsed.data.length,
      });
    } catch (error) {
      console.error('Error creating missing categories:', error);
      toast({
        title: 'Hata',
        description: 'Eksik kategoriler eklenirken bir hata oluştu.',
        variant: 'destructive',
      });
    } finally {
      setCreatingMissing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Download Template */}
      <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div>
          <h4 className="font-semibold text-blue-900">CSV Şablonu İndir</h4>
          <p className="text-sm text-blue-700">
            Şablonu indirip doldurarak toplu ürün yüklemesi yapabilirsiniz.
          </p>
          <p className="text-sm text-blue-700 mt-1">
            Görsel URL'lerini "images" alanına virgülle ayırarak ekleyebilirsiniz.
          </p>
        </div>
        <Button variant="outline" onClick={generateCSVTemplate}>
          <Download size={18} className="mr-2" />
          Şablon İndir
        </Button>
      </div>

      {/* File Upload */}
      <div>
        <label className="block text-sm font-medium mb-2">CSV Dosyası Seç</label>
        <div className="flex gap-4">
          <Input
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="flex-1"
          />
          {file && (
            <Button onClick={parseCSV} disabled={parsing}>
              {parsing ? 'İşleniyor...' : 'İşle'}
            </Button>
          )}
        </div>
        {file && (
          <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
            <span>{file.name}</span>
            <button
              onClick={() => {
                setFile(null);
                setResults(null);
              }}
              className="text-red-600 hover:text-red-700"
            >
              <X size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Results */}
      {results && (
        <div
          className={`p-4 rounded-lg border ${
            results.success
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-start gap-3">
            {results.success ? (
              <CheckCircle className="text-green-600 mt-0.5" size={20} />
            ) : (
              <AlertCircle className="text-red-600 mt-0.5" size={20} />
            )}
            <div className="flex-1">
              {results.success ? (
                <>
                  <h4 className="font-semibold text-green-900">CSV Başarıyla İşlendi</h4>
                  <p className="text-sm text-green-700 mt-1">
                    {results.validCount} ürün yüklenmeye hazır.
                  </p>
                  <Button onClick={handleUpload} disabled={uploading} className="mt-4">
                    {uploading ? 'Yükleniyor...' : `${results.validCount} Ürünü Yükle`}
                  </Button>
                </>
              ) : (
                <>
                  <h4 className="font-semibold text-red-900">Hata Bulundu</h4>
                  <ul className="text-sm text-red-700 mt-1 list-disc list-inside">
                    {results.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Dialog open={isMissingModalOpen} onOpenChange={setIsMissingModalOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Eksik Kategoriler Bulundu</DialogTitle>
            <DialogDescription>
              CSV’de tanımlı bazı kategori/alt kategori sistemde yok. Eklemek ister misiniz?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            {missingInfo?.categories?.length > 0 && (
              <div>
                <div className="font-semibold text-gray-900">Kategoriler</div>
                <div className="text-gray-600">
                  {missingInfo.categories.map((item) => item.name).join(', ')}
                </div>
              </div>
            )}
            {missingInfo?.subcategories?.length > 0 && (
              <div>
                <div className="font-semibold text-gray-900">Alt Kategoriler</div>
                <div className="text-gray-600">
                  {missingInfo.subcategories
                    .map((item) => `${item.parentName} > ${item.name}`)
                    .join(', ')}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsMissingModalOpen(false);
              }}
            >
              İptal
            </Button>
            <Button onClick={handleCreateMissingCategories} disabled={creatingMissing}>
              {creatingMissing ? 'Ekleniyor...' : 'Ekle ve Devam Et'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductBulkUpload;
